import {
  arrayHasValues,
  convertToString,
  isDefined,
} from "@timeedit/registration-shared";
import { Tag, Tooltip } from "@timeedit/ui-components";
import {
  colorError,
  colorPrimary,
} from "@timeedit/ui-components/theme/variables";
import cn from "classnames";
import { WaitlistTag } from "../WaitlistTag";
import "./multiProgress.scss";

type Translations = {
  noData: string;
  inWaitlistTooltip: string;
  leavingWaitlistDescription: string;
  waitlist: string;
};

type MultiProgressProps = {
  value: number;
  end: number;
  max?: number;
  compact?: boolean;
  explainValue?: string;
  explainEnd?: string;
  explainMax?: string;
  translations: Translations;
  formatHelp: () => string;
  className?: string;
  inWaitlist?: number[];
  leaving?: number[];
  alwaysShowWaitlist?: boolean;
};

function limitRange(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function calcPercent(taken: number, max?: number): number {
  if (!isDefined(max)) return 50;
  return roundUpSmallValues(limitRange((taken / max) * 100, 0, 100));
}

function strokeColor(taken: number, max?: number) {
  if (!isDefined(max)) return colorPrimary;
  const open = max - taken;
  if (open === 0) return colorPrimary;
  if (open > 0) return colorPrimary;
  return colorError;
}

function roundUpSmallValues(value: number): number {
  if (value > 0 && value < 1) {
    return 1;
  }
  return value;
}

export function MultiProgress(props: MultiProgressProps): React.JSX.Element {
  const textHelp = props.formatHelp();
  const wrapped =
    textHelp.length < 20
      ? "multiprogress-help multiprogress-text"
      : "multiprogress-help";
  const help = props.compact ? "" : <span className={wrapped}>{textHelp}</span>;
  const percent = calcPercent(props.value, props.max);
  const percentEnd = calcPercent(props.end, props.max);
  const textMax = isDefined(props.max)
    ? convertToString(props.max)
    : props.translations.noData;
  const toolTipMax = isDefined(props.explainMax)
    ? `\n${convertToString(props.explainMax)}: ${textMax}`
    : "";
  const toolTipBuffer =
    props.end === 0
      ? ""
      : `\n${convertToString(props.explainEnd)}: ${props.end}`;

  const showWaitList =
    props.alwaysShowWaitlist ||
    arrayHasValues(props.inWaitlist) ||
    arrayHasValues(props.leaving);

  const toolWaitlist = showWaitList
    ? `\n${props.translations.inWaitlistTooltip}: ${
        props.inWaitlist?.length ?? 0
      }` +
      `\n${props.translations.leavingWaitlistDescription}: ${
        props.leaving?.length ?? 0
      }`
    : "";

  const tooltipText = `${convertToString(props.explainValue)}: ${
    props.value
  }${toolTipMax}${toolTipBuffer}${toolWaitlist}`;

  const presentMaxTag = isDefined(props.max) ? (
    textMax
  ) : (
    <Tag.StatusTag state="Default">{textMax}</Tag.StatusTag>
  );

  return (
    <Tooltip styles={{ root: { whiteSpace: "pre-line" } }} title={tooltipText}>
      <div className={cn("multiprogress-base", props.className)}>
        <div className="multiprogress-line">
          <div className="multiprogress-inner">
            <div
              className="multiprogress-bar-right"
              style={{ width: `${percentEnd}%` }}
            />
            <div
              className="multiprogress-bar-left"
              style={{
                width: `${percent}%`,
                backgroundColor: strokeColor(props.value, props.max),
              }}
            />
            <div className="multiprogress-bar-base" />
          </div>
        </div>
        <span className="multiprogress-text">
          {props.value} / {presentMaxTag}
        </span>
        {help}
        <WaitlistTag
          alwaysDisplay={props.alwaysShowWaitlist}
          inWaitlist={props.inWaitlist?.length}
          leaving={props.leaving?.length}
        />
      </div>
    </Tooltip>
  );
}
